<script setup>
import { ref, useSlots, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
const route = useRoute()
const router = useRouter()
import Tab from './Tab.vue'

const isRTL = computed(() => {
    // console.log('Prebook Tabs: Tab rtl value is fixed. Change it when needed.')

    return document.querySelector('html').getAttribute('dir') == 'rtl' ? true : false
})

const props = defineProps({
    default: {
        type: String,
        default: null,
    },
    tabWrapperClass: {
        type: String,
    },
    tabClass: {
        type: String
    },
    design: {
        type: String,
        default: 'box',
        validator: (value) => {
            return ['box', 'underline'].indexOf(value) !== -1
        }
    },
    queryName: {
        type: String,
        default: 'tab'
    }
})

const defaultSlots = useSlots().default()

// Get all tabs from slots.
const getTabs = computed(() => {
    return defaultSlots.filter(slot => slot.type === Tab).map(tab => {
        return {
            id: tab.props.id,
            component: tab,
            slot: tab.children,
        }
    })
})

const defaultTab = computed(() => {
    if (route.query[props.queryName]) {
        return route.query[props.queryName]
    }

    return props.default || getTabs.value[0]?.props?.id
})

// Get selected tab. Use as state.
const selectedTab = ref(defaultTab.value)

// watch route for query change and update selected tab
watch(route, () => {
    selectedTab.value = route.query[props.queryName]
    // console.log('watch', selectedTab.value, route.query.tab)
})

const emit = defineEmits(['update:tab'])
const activeTab = computed({
    get: () => selectedTab.value,
    set: (value) => {
        selectedTab.value = value
        emit('update:tab', value)
    }
})

const setTab = (tab) => {
    selectedTab.value = tab
    if (props.queryName != 'tab') {
        setTimeout(() => {
            router.push({
                query: {
                    ...route.query,
                    [props.queryName]: tab,
                }
            })
        }, 100)
    } else {
        router.push({
            query: {
                ...route.query,
                [props.queryName]: tab,
            }
        })
    }

    activeTab.value = tab
}

onMounted(() => {
    setTab(defaultTab.value)
})

/**
 * helper function to calculate bar style based on the active tab or hover tab.
 */
const calculateBarStyle = (tabId) => {
    let padding = props.design === 'underline' ? 0 : 4
    let totalItems = getTabs.value.length || 1

    // box width with adjusted padding
    let boxWidth = `calc(${100 / totalItems}% - (${padding}px * 2))`
    let style = `width: ${boxWidth} !important;`

    // left position
    let selectedIndex = getTabs.value.findIndex(tab => tab.id == tabId)
    let isLastTab = selectedIndex === totalItems - 1
    let boxLeft

    if (selectedIndex === 0) {
        boxLeft = `${padding}px`  // First tab
    } else if (isLastTab && props.design === 'underline') {
        // last tab and underline design
        boxLeft = `calc((${100 / totalItems}% * ${selectedIndex}) + 4px)`
    } else {
        boxLeft = `calc((${100 / totalItems}% * ${selectedIndex}) + ${padding}px)`
    }

    // console.log(isRTL.value)
    if (isRTL.value) {
        style += `right: ${boxLeft} !important;`
    } else {
        style += `left: ${boxLeft} !important;`
    }

    // height based on props.design
    style += `height: ${props.design === 'underline' ? '3px' : 'calc(100% - 7px)'} !important;`

    return style
}

const activeBarStyle = computed(() => calculateBarStyle(selectedTab.value))

// hover functionality
const hover = ref(false)
const hoverActive = ref(null)

const setHover = (tab) => {
    hoverActive.value = tab
    hover.value = true
}

const removeHover = () => {
    hoverActive.value = null
    hover.value = false
}

const hoverActiveBarStyle = computed(() => hoverActive.value ? calculateBarStyle(hoverActive.value) : null)

// Accessibility.
const nextTab = () => {
    const index = getTabs.value.findIndex(tab => tab === selectedTab.value)

    if (index < getTabs.value.length - 1) {
        setTab(getTabs.value[index + 1].props.id)
    } else {
        setTab(getTabs.value[0].props.id)
    }
}

const visibility = computed(() => {
    return getTabs.value.length == 1 ? 'hidden' : ''
})
</script>

<template>
    <div>
        <div class="relative flex items-center gap-2 p-1 rounded-lg mb-7" :class="[props.design == 'underline' ? '' : 'border border-[#F2F4F7] bg-[#F9FAFB]'], tabWrapperClass, visibility">
            <div v-if="props.design == 'underline'" class="absolute z-0 transition-all duration-300 rounded-lg w-full bg-[#D0D5DD] h-[1px] bottom-0"></div>
            <div class="absolute z-0 transition-all duration-300 rounded-md" :style="`${activeBarStyle}`" :class="[props.design == 'underline' ? 'bg-[#7f56d9] bottom-0' : 'top-[3px] bg-white shadow-[0px_1px_2px_0px_#1018280F,0px_1px_3px_0px_#1018281A]'], tabClass"></div>
            <transition name="fade">
                <div v-if="hover" class="absolute z-0 transition-all duration-300 rounded-md" :style="`${hoverActiveBarStyle}`" :class="[props.design == 'underline' ? 'bg-[#7f56d9] bottom-0' : 'top-[3px] bg-white shadow-[0px_1px_2px_0px_#1018280F,0px_1px_3px_0px_#1018281A]'], tabClass"></div>
            </transition>
            <div @mouseenter="setHover(tab.id)" @mouseleave="removeHover" v-for="tab in getTabs" @click.prevent="setTab(tab.id)" @keydown.tab.prevent="nextTab" class="z-10 w-full px-3 py-2 text-sm font-semibold text-center capitalize transition-all duration-300 rounded-md outline-none cursor-pointer click-animation focus:outline-none whitespace-nowrap duration-400" :class="[
                selectedTab == tab.id
                    ? (props.design == 'underline'
                        ? 'text-[#7F56D9]'
                        : 'text-[#344054]')
                    : (props.design == 'underline')
                        ? 'text-[#667085] hover:text-[#7F56D9]'
                        : 'text-[#667085] hover:text-[#344054]',
                tabClass
            ]">
                <component :is="tab.component" />
            </div>
        </div>

        <slot :name="selectedTab"></slot>
    </div>
</template>
